/**
 * Presentational date-range popover body: two MM/DD/YYYY inputs, To label, and Done inline.
 * Validation and discard-on-dismiss live here; draft commit is via onChange from the parent.
 * When embedded, omits the section title and outer padding for use under a radio option.
 */
import { useEffect, useState } from "react";
import { Input } from "../../../../../../../components/inputs/input/input";
import FontBody2 from "../../../../../../../components/fonts/font-body2/font-body2";
import useDashboardTranslation from "../../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import {
    calendarRangeToIsoRangeString,
    compareCalendarDates,
    formatDate,
    isoRangeStringToCalendarRange,
    maskDefaultDatePattern,
    parseDateFormat,
} from "../../../../../../../utilities/date-utilities";
import { useRmaListFilterPopoverClose } from "../../rma-list-filter-popover-close-context";
import RmaListFilterRangeRow from "../range-row/rma-list-filter-range-row";
import "./rma-list-filter-date-range-content.scss";

export interface IRmaListFilterDateRangeContentProps {
    /** Committed ISO range string from the store (draft after Done). */
    value: string;
    timeZone: string;
    /** Optional override; defaults to parsing `value` in `timeZone`. */
    getInputValues?: (timeZone: string) => { from: string; to: string };
    isOpen?: boolean;
    /** Nested under a radio option — no title / outer padding. */
    embedded?: boolean;
    onChange: (isoRange: string) => void;
}

const defaultInputValuesFromIsoRange = (
    value: string,
    timeZone: string,
): { from: string; to: string } => {
    const range = isoRangeStringToCalendarRange(value, timeZone);
    if (!range) {
        return { from: "", to: "" };
    }
    return {
        from: formatDate(range.from),
        to: formatDate(range.to),
    };
};

const RmaListFilterDateRangeContent: React.FC<IRmaListFilterDateRangeContentProps> = ({
    value,
    timeZone,
    getInputValues,
    isOpen = false,
    embedded = false,
    onChange,
}) => {
    const onRequestClose = useRmaListFilterPopoverClose();
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const [fromInput, setFromInput] = useState("");
    const [toInput, setToInput] = useState("");

    useEffect(() => {
        const next = getInputValues
            ? getInputValues(timeZone)
            : defaultInputValuesFromIsoRange(value, timeZone);
        setFromInput(next.from);
        setToInput(next.to);
    }, [isOpen, value, timeZone, getInputValues]);

    const fromParts = parseDateFormat(fromInput);
    const toParts = parseDateFormat(toInput);
    const fromFilled = fromInput.trim().length > 0;
    const toFilled = toInput.trim().length > 0;
    const fromInvalid = fromFilled && !fromParts;
    const toInvalid = toFilled && !toParts;
    const bothValid = Boolean(fromParts && toParts);
    const rangeOrderInvalid = bothValid && compareCalendarDates(fromParts!, toParts!) > 0;
    const canSubmit = bothValid && !rangeOrderInvalid;

    const fromHasError = fromInvalid || rangeOrderInvalid;
    const toHasError = toInvalid || rangeOrderInvalid;

    const onDone = () => {
        if (!fromParts || !toParts || rangeOrderInvalid) {
            return;
        }
        onChange(calendarRangeToIsoRangeString(fromParts, toParts, timeZone));
        onRequestClose();
    };

    return (
        <div
            className={`rma-list-filter-date-range-content${
                embedded ? " rma-list-filter-date-range-content--embedded" : ""
            }`}
        >
            {!embedded &&
                <FontBody2>{getTranslation("FILTERS_DATE_CREATED_RANGE_TITLE", scope)}</FontBody2>
            }
            <RmaListFilterRangeRow
                fromInput={
                    <Input
                        placeholder="MM/DD/YYYY"
                        value={fromInput}
                        onChange={(value) => setFromInput(maskDefaultDatePattern(value))}
                        maxlength={10}
                        validationCallback={() => ({ isValid: !fromHasError, messages: [] })}
                    />
                }
                toInput={
                    <Input
                        placeholder="MM/DD/YYYY"
                        value={toInput}
                        onChange={(value) => setToInput(maskDefaultDatePattern(value))}
                        maxlength={10}
                        validationCallback={() => ({ isValid: !toHasError, messages: [] })}
                    />
                }
                canSubmit={canSubmit}
                onDone={onDone}
            />
        </div>
    );
};

export default RmaListFilterDateRangeContent;
